import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { ForeignBusinessContactItemModel } from 'forms/sections/foreign/business/contact/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import { isPVQForm } from 'helpers/form';
import RadioGroup from 'components/Form/RadioGroup';
import { foreignBusinessAffairsAgencyOptions, foreignBusinessAffairsFamilyOptions } from 'constants/enums/foreignBusinessOptions';
import PVQName from 'components/Form/PVQName';
import Radio from 'components/Form/Radio';
import NotApplicable from 'components/Form/NotApplicable';
import Text from 'components/Form/Text';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import Branch from 'components/Form/Branch';
import SubsequentContacts from './SubsequentContacts';

interface Props
  extends Partial<ForeignBusinessContactItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute: string
  formType?: string
}

export default class ContactItem extends ValidationElement<Props> {
  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignBusinessContactItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Location: this.props.Location,
      Date: this.props.Date,
      Governments: this.props.Governments,
      Establishment: this.props.Establishment,
      Representatives: this.props.Representatives,
      Purpose: this.props.Purpose,
      SubsequentContacts: this.props.SubsequentContacts,
      WhoContacted: this.props.WhoContacted, // PVQ Only
      AgencyType: this.props.AgencyType, // PVQ Only
      AgencyTypeOtherExplanation: this.props.AgencyTypeOtherExplanation, // PVQ Only
      AgencyName: this.props.AgencyName, // PVQ Only
      AgencyNameNotApplicable: this.props.AgencyNameNotApplicable, // PVQ Only
      RepresentativeName: this.props.RepresentativeName, // PVQ Only
      RepresentativeNameNotApplicable: this.props.RepresentativeNameNotApplicable, // PVQ Only
      LocationNotApplicable: this.props.LocationNotApplicable, // PVQ Only
      HasFuturePlansToContact: this.props.HasFuturePlansToContact, // PVQ Only
      HasFuturePlansToContactExplanation: this.props.HasFuturePlansToContactExplanation, // PVQ Only
      ...queue,
    });
  };

  updateName = (values: ForeignBusinessContactItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateLocation = (values: ForeignBusinessContactItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateDate = (values: ForeignBusinessContactItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateGovernments = (values: ForeignBusinessContactItemModel['Governments']) => {
    this.update({
      Governments: values,
    });
  };

  updateEstablishment = (values: ForeignBusinessContactItemModel['Establishment']) => {
    this.update({
      Establishment: values,
    });
  };

  updateRepresentatives = (values: ForeignBusinessContactItemModel['Representatives']) => {
    this.update({
      Representatives: values,
    });
  };

  updatePurpose = (values: ForeignBusinessContactItemModel['Representatives']) => {
    this.update({
      Purpose: values,
    });
  };

  updateSubsequentContacts = (values: ForeignBusinessContactItemModel['SubsequentContacts']) => {
    this.update({
      SubsequentContacts: values,
    });
  };

  updateWhoContacted = (value: ForeignBusinessContactItemModel['WhoContacted']) => {
    this.update({
      WhoContacted: value,
      Name: value?.value !== 'Myself' ? this.props.Name : {},
    });
  };

  updateAgencyType = (value: ForeignBusinessContactItemModel['AgencyType']) => {
    this.update({
      AgencyType: value,
      AgencyTypeOtherExplanation: value?.value === 'Other' ? this.props.AgencyTypeOtherExplanation : {},
    });
  };

  updateAgencyTypeOtherExplanation = (value: ForeignBusinessContactItemModel['AgencyTypeOtherExplanation']) => {
    this.update({
      AgencyTypeOtherExplanation: value,
    });
  };

  updateAgencyName = (values: ForeignBusinessContactItemModel['AgencyName']) => {
    this.update({
      AgencyName: values,
    });
  };

  updateAgencyNameNotApplicable = (
    value: ForeignBusinessContactItemModel['AgencyNameNotApplicable']
  ) => {
    this.update({
      AgencyName: value?.applicable ? this.props.AgencyName : {},
      AgencyNameNotApplicable: value,
    });
  };

  updateRepresentativeName = (values: ForeignBusinessContactItemModel['RepresentativeName']) => {
    this.update({
      RepresentativeName: values,
    });
  };

  updateRepresentativeNameNotApplicable = (
    value: ForeignBusinessContactItemModel['RepresentativeNameNotApplicable']
  ) => {
    this.update({
      RepresentativeName: value?.applicable ? this.props.RepresentativeName : {},
      RepresentativeNameNotApplicable: value,
    });
  };

  updateLocationNotApplicable = (
    value: ForeignBusinessContactItemModel['LocationNotApplicable']
  ) => {
    this.update({
      Location: value?.applicable ? this.props.Location : {},
      LocationNotApplicable: value,
    });
  };

  updateHasFuturePlansToContact = (values: ForeignBusinessContactItemModel['HasFuturePlansToContact']) => {
    this.update({
      HasFuturePlansToContact: values,
      HasFuturePlansToContactExplanation: values?.value === 'Yes' ? this.props.HasFuturePlansToContactExplanation : {},
    });
  };

  updateHasFuturePlansToContactExplanation = (
    values: ForeignBusinessContactItemModel['HasFuturePlansToContactExplanation']
  ) => {
    this.update({ HasFuturePlansToContactExplanation: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (

      <div className="foreign-business-contact-item">
        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.whoContacted')}
          className="who-was-contacted"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WhoContacted`}
        >
          <RadioGroup
            className="who-was-asked option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.WhoContacted?.value}
            attribute={`${attribute}.WhoContacted`}
          >
            {foreignBusinessAffairsFamilyOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateWhoContacted}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.WhoContacted?.value && this.props.WhoContacted?.value !== 'Myself'
          && (
            <Field
              title={i18n.t('foreign.pvq.business.contact.heading.name')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Name`}
            >
              <PVQName
                {...this.props.Name}
                name="Name"
                attribute={`${attribute}.Name`}
                aria-label={i18n.t('foreign.pvq.business.contact.heading.name')}
                onUpdate={this.updateName}
                onError={this.handleNameError}
                className="name"
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                showLastFirstMiddleNameOrder
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.governments')}
          attribute={`${attribute}.Governments`}
          help="foreign.business.contact.help.governments"
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            {...this.props.Governments}
            name="Governments"
            attribute={`${attribute}.Governments`}
            excludeUnitedStates
            onUpdate={this.updateGovernments}
            onError={this.props.onError}
            className="foreign-business-contact-governments"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.agencyType')}
          className="agency-type"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AgencyType`}
        >
          <RadioGroup
            className="agency-type option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.AgencyType?.value}
            attribute={`${attribute}.AgencyType`}
          >
            {foreignBusinessAffairsAgencyOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateAgencyType}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.AgencyType?.value === 'Other'
          && (
            <Field
              title={i18n.t('foreign.pvq.business.contact.heading.explanation')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AgencyTypeOtherExplanation`}
            >
              <Textarea
                {...this.props.AgencyTypeOtherExplanation}
                name="AgencyTypeOtherExplanation"
                attribute={`${attribute}.AgencyTypeOtherExplanation`}
                onUpdate={this.updateAgencyTypeOtherExplanation}
                onError={this.props.onError}
                className="venture-agency-type-other-explanation"
                required={this.props.required}
                ScrollIntoView={this.props.scrollIntoView}
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.agencyName')}
          help="foreign.business.contact.help.agencyName"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AgencyName`}
        >
          <NotApplicable
            {...this.props.AgencyNameNotApplicable}
            name="AgencyNameNotApplicable"
            className="foreign-venture-citizenship-not-applicable"
            onUpdate={this.updateAgencyNameNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.pvq.business.contact.para.or')}
            label={i18n.t('foreign.pvq.business.contact.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.AgencyNameNotApplicable`}
            includeNAConfirmation={Boolean(this.props.AgencyName?.value)}
          >
            <Text
              {...this.props.AgencyName}
              name="AgencyName"
              onUpdate={this.updateAgencyName}
              onError={this.props.onError}
              className="agency-name"
              required={this.props.required}
              attribute={`${attribute}.AgencyName`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.purpose')}
          attribute={`${attribute}.Purpose`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Purpose}
            name="Purpose"
            attribute={`${attribute}.Purpose`}
            onUpdate={this.updatePurpose}
            onError={this.props.onError}
            className="foreign-business-contact-purpose"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.representativeName')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RepresentativeName`}
        >
          <NotApplicable
            {...this.props.RepresentativeNameNotApplicable}
            name="RepresentativeNameNotApplicable"
            className="foreign-venture-citizenship-not-applicable"
            onUpdate={this.updateRepresentativeNameNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.pvq.business.contact.para.or')}
            label={i18n.t('foreign.pvq.business.contact.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.RepresentativeNameNotApplicable`}
          >
            <PVQName
              {...this.props.RepresentativeName}
              name="RepresentativeName"
              attribute={`${attribute}.RepresentativeName`}
              aria-label={i18n.t('foreign.pvq.business.contact.heading.representativeName')}
              onUpdate={this.updateRepresentativeName}
              onError={this.handleNameError}
              className="representative-name"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              hideMiddleName
              showLastFirstMiddleNameOrder
              showFirstUnknown
              showLastUnknown
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.contact.heading.date')}
          attribute={`${attribute}.Date`}
          help="foreign.pvq.business.contact.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="Date"
            attribute={`${attribute}.Date`}
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="foreign-business-contact-date"
            required={this.props.required}
            hideDay
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Location`}
          adjustFor="address"
        >
          <NotApplicable
            {...this.props.LocationNotApplicable}
            name="LocationNotApplicable"
            className="foreign-contact-location-not-applicable"
            onUpdate={this.updateLocationNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.pvq.business.contact.para.or')}
            label={i18n.t('foreign.pvq.business.contact.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.LocationNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Location?.value)}
          >
            <PVQLocationUnknown
              {...this.props.Location}
              addressUnknown={this.props.LocationNotApplicable}
              addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
              attribute={`${attribute}.Location`}
              name="foreign-venture-location"
              onUpdate={this.updateLocation} // eslint-disable-line @typescript-eslint/unbound-method
              onUpdateAddressUnknown={this.updateLocationNotApplicable}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              required={this.props.required}
              showCityUnknown
              showZipcodeUnknown
              showStreetUnknown
              showInternationalStreet
              showMilitaryInstallation={false}
              label={i18n.t(`foreign.pvq.business.contact.heading.address`)}
              domesticTitle={i18n.t(`foreign.pvq.business.contact.heading.addressDomesticTitle`)}
              foreignTitle={i18n.t(`foreign.pvq.business.contact.heading.addressForeignTitle`)}
            />
          </NotApplicable>
        </Field>

        <Branch
          {...this.props.HasFuturePlansToContact}
          name="HasFuturePlansToContact"
          attribute={`${attribute}.HasFuturePlansToContact`}
          label={i18n.t('foreign.pvq.business.contact.heading.hasFuturePlansToContact')}
          labelSize="h4"
          className="has-obligations"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHasFuturePlansToContact}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.HasFuturePlansToContact?.value === 'Yes' && (
          <Field
            title={i18n.t('foreign.pvq.business.contact.heading.explanation')}
            attribute={`${attribute}.HasFuturePlansToContactExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.HasFuturePlansToContactExplanation}
              name={`${attribute}.HasFuturePlansToContactExplanation`}
              attribute="HasFuturePlansToContactExplanation"
              className="HasFuturePlansToContactExplanation"
              onUpdate={this.updateHasFuturePlansToContactExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

      </div>
    ) : (
      <div className="foreign-business-contact-item">
        <Field
          title={i18n.t('foreign.business.contact.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            attribute={`${attribute}.Name`}
            aria-label="Provide the name of the individual involved in the contact"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="foreign-business-contact-name"
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('foreign.business.contact.heading.location')}
          attribute={`${attribute}.Location`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="Location"
            attribute={`${attribute}.Location`}
            {...this.props.Location}
            onUpdate={this.updateLocation}
            onError={this.props.onError}
            layout={Location.US_CITY_STATE_ZIP_INTERNATIONAL_CITY}
            help=""
            label={i18n.t('foreign.business.contact.label.location')}
            className="birthplace foreign-business-contact-location"
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.contact.heading.date')}
          attribute={`${attribute}.Date`}
          help="foreign.business.contact.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="Date"
            attribute={`${attribute}.Date`}
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="foreign-business-contact-date"
            required={this.props.required}
            hideDay
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.contact.heading.governments')}
          attribute={`${attribute}.Governments`}
          help="foreign.business.contact.help.governments"
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            name="Governments"
            attribute={`${attribute}.Governments`}
            {...this.props.Governments}
            excludeUnitedStates
            onUpdate={this.updateGovernments}
            onError={this.props.onError}
            className="foreign-business-contact-governments"
            multiple
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.contact.heading.establishment')}
          attribute={`${attribute}.Establishment`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Establishment"
            attribute={`${attribute}.Establishment`}
            {...this.props.Establishment}
            onUpdate={this.updateEstablishment}
            onError={this.props.onError}
            className="foreign-business-contact-establishment"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.contact.heading.representatives')}
          attribute={`${attribute}.Representatives`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Representatives"
            attribute={`${attribute}.Representatives`}
            {...this.props.Representatives}
            onUpdate={this.updateRepresentatives}
            onError={this.props.onError}
            className="foreign-business-contact-representatives"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.contact.heading.purpose')}
          attribute={`${attribute}.Purpose`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Purpose"
            attribute={`${attribute}.Purpose`}
            {...this.props.Purpose}
            onUpdate={this.updatePurpose}
            onError={this.props.onError}
            className="foreign-business-contact-purpose"
            required={this.props.required}
          />
        </Field>

        <SubsequentContacts
          {...this.props.SubsequentContacts}
          onUpdate={this.updateSubsequentContacts}
          onError={this.props.onError}
          name="SubsequentContacts"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SubsequentContacts`}
        />
      </div>
    );
  }
}

ContactItem.defaultProps = {
  Name: {},
  Location: {},
  Date: {},
  Governments: {},
  Establishment: {},
  Representatives: {},
  Purpose: {},
  SubsequentContacts: {},
  WhoContacted: {}, // PVQ Only
  AgencyType: {}, // PVQ Only
  AgencyTypeOtherExplanation: {}, // PVQ Only
  AgencyName: {}, // PVQ Only
  AgencyNameNotApplicable: { applicable: true }, // PVQ Only
  RepresentativeName: {}, // PVQ Only
  RepresentativeNameNotApplicable: { applicable: true }, // PVQ Only
  LocationNotApplicable: { applicable: true }, // PVQ Only
  HasFuturePlansToContact: {}, // PVQ Only
  HasFuturePlansToContactExplanation: {}, // PVQ Only
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
